// Pure builders for ExpressCheckout payloads (one-time, recurring, tokenization).
// No side effects, no I/O — fully unit-testable.
//
// Endpoints:
//   - MoneyIn/getpaid     (one_time)      => buildOneTimePayload
//   - Subscription/add    (autopay)       => buildRecurringPayload
//   - TokenStorage/add    (tokenization)  => buildTokenizationPayload
//
// The processor (processWalletPayment) routes based on getMode(config).

import { EMBEDDED_COMPONENT_SOURCE } from "../../constants/paymentSource";

// Returns the canonical mode for a config.
// mode must be set on expressCheckout (via normalizeExpressCheckoutConfig or explicitly).
// Defaults to "one_time" when absent for safety.
export function getMode(config) {
  const ec = config?.expressCheckout;
  return ec?.mode ?? 'one_time';
}

export function buildPaymentMethod({
  walletType,
  walletToken,
  cardHolder,
  saveIfSuccess,
} = {}) {
  const paymentMethod = {
    method: "wallet",
    walletType,
    walletToken,
  };
  if (cardHolder !== undefined && cardHolder !== null && cardHolder !== "") {
    paymentMethod.cardHolder = cardHolder;
  }
  if (saveIfSuccess === true) {
    paymentMethod.saveIfSuccess = true;
  }
  return paymentMethod;
}

function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function roundToCents(value) {
  return Math.round(value * 100) / 100;
}

export function buildPaymentDetails({
  amount,
  fee,
  currency,
  splitFunding,
  recurring = false,
} = {}) {
  const numericAmount = toNumberOrZero(amount);
  const numericFee = toNumberOrZero(fee);
  const totalAmount = roundToCents(numericAmount + numericFee);

  if (recurring) {
    return {
      totalAmount,
      serviceFee: numericFee,
      currency: currency || "USD",
      categories: [],
    };
  }

  return {
    totalAmount,
    serviceFee: numericFee.toFixed(2),
    ...(splitFunding ? { splitFunding } : {}),
  };
}

// Translates the partner-facing `expressCheckout.autopay` config into the
// `scheduleDetails` envelope the Payabli Subscription/add endpoint expects.
export function buildScheduleDetails(autopay = {}) {
  const details = {
    frequency: autopay.frequency,
    startDate: autopay.startDate,
    untilCancel: autopay.untilCancel === true,
  };
  if (autopay.endDate !== undefined && autopay.endDate !== null) {
    details.endDate = autopay.endDate;
  }
  return details;
}

export function buildRecurringPayload({
  config,
  walletType,
  walletToken,
  cardHolder,
  customerData,
} = {}) {
  const expressCheckout = config?.expressCheckout ?? {};
  const invoiceData = buildInvoiceData(expressCheckout);

  return {
    entryPoint: config?.entryPoint,
    paymentMethod: buildPaymentMethod({ walletType, walletToken, cardHolder }),
    paymentDetails: buildPaymentDetails({
      amount: expressCheckout.amount,
      fee: expressCheckout.fee,
      currency: expressCheckout.currency,
      recurring: true,
    }),
    scheduleDetails: buildScheduleDetails(expressCheckout.autopay),
    source: EMBEDDED_COMPONENT_SOURCE,
    customerData,
    ...(invoiceData !== undefined ? { invoiceData } : {}),
  };
}

// Copies the invoiceData object (BillData) from config to the payload.
// Null field values are preserved — the API accepts them.
// Returns undefined when invoiceData is absent or not a plain object.
function buildInvoiceData(expressCheckout) {
  const invoiceData = expressCheckout?.invoiceData;
  if (invoiceData === null || invoiceData === undefined) return undefined;
  const proto = Object.getPrototypeOf(invoiceData);
  if (typeof invoiceData !== 'object' || Array.isArray(invoiceData) ||
      (proto !== Object.prototype && proto !== null)) return undefined;
  return { ...invoiceData };
}

export function buildOneTimePayload({
  config,
  walletType,
  walletToken,
  cardHolder,
  customerData,
} = {}) {
  const expressCheckout = config?.expressCheckout ?? {};
  const invoiceData = buildInvoiceData(expressCheckout);
  return {
    entryPoint: config?.entryPoint,
    paymentMethod: buildPaymentMethod({
      walletType,
      walletToken,
      cardHolder,
      saveIfSuccess: expressCheckout.saveIfSuccess === true,
    }),
    paymentDetails: buildPaymentDetails({
      amount: expressCheckout.amount,
      fee: expressCheckout.fee,
      splitFunding: expressCheckout.splitFunding,
      recurring: false,
    }),
    source: EMBEDDED_COMPONENT_SOURCE,
    customerData,
    ...(invoiceData !== undefined ? { invoiceData } : {}),
  };
}

// Builds the TokenStorage/add payload for vaulting a wallet payment method.
// fallbackAuth: true delegates the authorization step to the backend.
export function buildTokenizationPayload({
  config,
  walletType,
  walletToken,
  cardHolder,
  customerData,
} = {}) {
  const expressCheckout = config?.expressCheckout ?? {};
  const payload = {
    entryPoint: config?.entryPoint,
    fallbackAuth: true,
    fallbackAuthAmount: expressCheckout.fallbackAuthAmount ?? 0.01,
    paymentMethod: buildPaymentMethod({ walletType, walletToken, cardHolder }),
    source: EMBEDDED_COMPONENT_SOURCE,
  };
  if (customerData && Object.keys(customerData).length > 0) {
    payload.customerData = customerData;
  }
  if (expressCheckout.methodDescription) {
    payload.methodDescription = expressCheckout.methodDescription;
  }
  return payload;
}
